import {
  type AccountData,
  type AccountNote,
  type AuditLog,
  isDefined,
  isNotDefined,
  isNotEmpty,
  type Workflow,
} from '@terros/common'
import { type TimeWindow } from './ownershipWindows'
import { type TeamChange, toTeamChanges, toUserChanges, type UserChange } from './ownershipChanges'
import { isAreaAssignmentSave, isImportSave, isWrittenWith } from './historySource'
import { type CreationAssignment, isCreationChange, toCreationAssignment } from './creationAssignment'
import { hidePreviousAssignment, isVisible, toUnassignmentAtWindowEnd } from './activityWindows'
import { isExplainedSave, withAreaRun } from './activityAttribution'
import { type AccountJournalEntry, buildAccountJournal } from './accountJournal'

type WithActor = {
  /** The save that made this change, when one lines up exactly */
  actor?: AuditLog
  /** Set on the latest area assignment: the run's timestamp in that area's run history (`account.assignmentDate`) */
  areaRunAt?: number
}

export type AccountCreation = {
  /** The save that created the account: the creator, or the import */
  actor: AuditLog
  isImport: boolean
  /** The owner, closer and team the account started with */
  assignment: CreationAssignment
}

export type WorkflowActivity = {
  type: 'workflow'
  timestamp: number
  entry: AccountJournalEntry
  /** Set when this update is the one that created the account */
  creation?: AccountCreation
}
export type NoteActivity = { type: 'note'; timestamp: number; note: AccountNote }
export type UserChangeActivity = UserChange & WithActor
export type TeamChangeActivity = TeamChange & WithActor
export type CreatedActivity = AccountCreation & { type: 'created'; timestamp: number }
/** Saves nothing else explains; consecutive ones are grouped */
export type SavesActivity = { type: 'saves'; timestamp: number; actors: AuditLog[] }

export type AccountActivityItem =
  | WorkflowActivity
  | NoteActivity
  | UserChangeActivity
  | TeamChangeActivity
  | CreatedActivity
  | SavesActivity

type BuildAccountActivityProps = {
  workflow: Workflow
  account:
    | Pick<
        AccountData,
        | 'workflowHistory'
        | 'importId'
        | 'modifiedBy'
        | 'notes'
        | 'ownerHistory'
        | 'closerHistory'
        | 'teamHistory'
        | 'assignmentDate'
      >
    | undefined
  /** When set, only activity inside these windows shows (a rep's own stretches of owning the account) */
  visibleWindows?: TimeWindow[]
}

/** Workflow history, notes, owner/closer/team changes and leftover saves in one oldest-first list */
export const buildAccountActivity = ({
  workflow,
  account,
  visibleWindows,
}: BuildAccountActivityProps): AccountActivityItem[] => {
  if (isNotDefined(account)) return []
  const audits = [...(account.modifiedBy ?? [])].sort((a, b) => a.timestamp - b.timestamp)
  // Audit logs are pruned, but the earliest one (the account's creation) is always kept
  const creation = audits.at(0)
  const journal = buildAccountJournal({ workflow, account })
  // Imports write a note per row, often with an empty note column
  const notes = (account.notes ?? []).filter((note) => isNotEmpty(note.text.trim()))
  const changes = [
    ...toUserChanges({ type: 'owner', history: account.ownerHistory }),
    ...toUserChanges({ type: 'closer', history: account.closerHistory }),
    ...toTeamChanges(account.teamHistory),
  ]

  const creationChanges = changes.filter((change) => isCreationChange({ change, creation }))
  const accountCreation: AccountCreation | undefined = isDefined(creation)
    ? {
        actor: creation,
        // `importId` stays on an existing account an import later updates, so ask the creating save itself
        isImport: isImportSave(creation),
        assignment: toCreationAssignment({ changes: creationChanges, creation }),
      }
    : undefined
  const workflowItems = journal.map((entry, index) =>
    toWorkflowActivity({ entry, isFirst: index === 0, creation: accountCreation })
  )
  const isCreationFolded = workflowItems.some((item) => isDefined(item.creation))
  const createdItems: CreatedActivity[] =
    isDefined(accountCreation) && !isCreationFolded
      ? [{ type: 'created', timestamp: accountCreation.actor.timestamp, ...accountCreation }]
      : []
  const leftoverSaves: SavesActivity[] = audits
    .filter((audit) => audit !== creation && !isExplainedSave({ audit, journal, notes, changes }))
    // Area runs only (un)assign owners and teams; one that matches no change predates team history, so says nothing
    .filter((audit) => !isAreaAssignmentSave(audit))
    .map((audit) => ({ type: 'saves', timestamp: audit.timestamp, actors: [audit] }))

  const items: AccountActivityItem[] = [
    ...workflowItems,
    ...notes.map((note): NoteActivity => ({ type: 'note', timestamp: note.timestamp, note })),
    ...withAreaRun({
      changes: changes
        .filter((change) => !creationChanges.includes(change))
        .map((change) => ({ ...change, actor: audits.find((audit) => audit.timestamp === change.timestamp) })),
      assignmentDate: account.assignmentDate,
    }),
    ...createdItems,
    ...leftoverSaves,
  ]
  return groupSaves(
    items
      // Everything outside the windows goes, notes included; the assignment that opens each window stays
      .filter((item) => isVisible({ item, visibleWindows }))
      .map((item) => hidePreviousAssignment({ item, visibleWindows }))
      .map((item) => toUnassignmentAtWindowEnd({ item, visibleWindows }))
      .sort((a, b) => a.timestamp - b.timestamp)
  )
}

type ToWorkflowActivityProps = {
  entry: AccountJournalEntry
  isFirst: boolean
  creation: AccountCreation | undefined
}

const toWorkflowActivity = ({ entry, isFirst, creation }: ToWorkflowActivityProps): WorkflowActivity => ({
  type: 'workflow',
  timestamp: entry.timestamp,
  entry,
  creation: isCreationEntry({ entry, isFirst, creation }) ? creation : undefined,
})

/**
 * Imports stamp history with the time each row was read, and save the account much later,
 * so an imported account's first update is the import whatever the gap. Otherwise the
 * creating save lands right after the entries it wrote.
 */
const isCreationEntry = ({ entry, isFirst, creation }: ToWorkflowActivityProps): boolean => {
  if (isNotDefined(creation)) return false
  if (creation.isImport) return isFirst
  return isWrittenWith(creation.actor, entry.timestamp)
}

/** Merges consecutive saves into one group; returns new groups so cached activity is never mutated */
export const groupSaves = (items: AccountActivityItem[]): AccountActivityItem[] =>
  items.reduce<AccountActivityItem[]>((acc, item) => {
    const prev = acc.at(-1)
    if (item.type === 'saves' && prev?.type === 'saves') {
      return [...acc.slice(0, -1), { ...prev, actors: [...prev.actors, ...item.actors] }]
    }
    return [...acc, item]
  }, [])
